import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSubscription, writeState } from "@/hooks/useSubscription";
import { useIsStaff } from "@/hooks/useStaffRoles";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, ShieldCheck, Sparkles } from "lucide-react";

import { tr } from "@/lib/tr";
// Reading, reports, export and backup stay reachable while a workspace is frozen.
const ALLOWED_PREFIXES = ["/billing", "/profile", "/auth", "/admin", "/help", "/reports", "/companies"];

export const SubscriptionGuard = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation();
  const isRtl = i18n.language === "ar" || i18n.language === "ur";
  const { data: sub, isLoading } = useSubscription();
  const { isStaff } = useIsStaff();
  const location = useLocation();

  const state = writeState(sub);

  if (isLoading || !sub) return <>{children}</>;
  if (isStaff) return <>{children}</>;
  if (state === "allowed") return <>{children}</>;
  if (ALLOWED_PREFIXES.some((p) => location.pathname.startsWith(p))) return <>{children}</>;

  const title =
    state === "canceled"
      ? tr("اشتراكك ملغى", "Your subscription is canceled", "Votre abonnement est annulé")
      : state === "past_due_locked"
        ? tr("انتهت مهلة السداد", "The payment grace period has ended", "Le délai de paiement est écoulé")
        : tr("حسابك مُجمَّد مؤقتاً", "Your account is frozen", "Votre compte est gelé");

  const body =
    state === "canceled"
      ? tr(
          "تم إلغاء اشتراكك، لذلك التسجيل والترحيل متوقفان. اختر باقة لإعادة التفعيل، ولن تُحذف أي من بياناتك.",
          "Your subscription was canceled, so recording and posting are paused. Choose a plan to reactivate. None of your data has been deleted.",
          "Votre abonnement a été annulé, la saisie et la validation sont suspendues. Choisissez un forfait pour réactiver. Aucune de vos données n'a été supprimée."
        )
      : state === "past_due_locked"
        ? tr(
            "لم يُستكمل الدفع خلال مهلة السماح، لذلك التسجيل والترحيل متوقفان. أكمل الدفع لإعادة التفعيل، ولن تُحذف أي من بياناتك.",
            "Payment was not completed within the grace period, so recording and posting are paused. Complete the payment to resume. None of your data has been deleted.",
            "Le paiement n'a pas été effectué dans le délai de grâce, la saisie et la validation sont suspendues. Réglez le paiement pour reprendre. Aucune de vos données n'a été supprimée."
          )
        : tr(
            "انتهت فترتك التجريبية أو اشتراكك. اختر باقة لإعادة تفعيل الكتابة. لن تُحذف أي من بياناتك.",
            "Your trial or subscription has ended. Choose a plan to resume. None of your data has been deleted.",
            "Votre essai ou abonnement a expiré. Choisissez un forfait pour reprendre l'activité. Aucune de vos données n'a été supprimée."
          );

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center p-6" dir={isRtl ? "rtl" : undefined}>
      <Card className="max-w-xl w-full border-primary/30">
        <CardContent className="pt-8 pb-6 text-center space-y-5">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold font-display">{title}</h1>
          <p className="text-muted-foreground">{body}</p>
          <p className="text-sm text-muted-foreground">
            {tr(
              "ما زال بإمكانك الاطلاع على بياناتك والتقارير وتصديرها أثناء التجميد.",
              "You can still view your data, run reports and export them while frozen.",
              "Vous pouvez toujours consulter vos données, générer des rapports et les exporter pendant le gel."
            )}
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
            <ShieldCheck className="h-4 w-4" />
            {tr("بياناتك محفوظة بالكامل", "Your data is fully preserved", "Vos données sont entièrement conservées")}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
            <Button asChild size="lg">
              <Link to="/billing/plans">
                <Sparkles className="me-2 h-4 w-4" />
                {tr("اختر باقة", "Choose a plan", "Choisir un forfait")}
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/billing">{tr("تفاصيل الاشتراك", "Subscription details", "Détails de l'abonnement")}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

